import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Customized,
} from 'recharts';

/**
 * GBMChart — fan chart showing GBM expected path + ±1σ/2σ/3σ confidence bands.
 *
 * Props:
 *   bands    — array of {day, expected, upper1, lower1, upper2, lower2, upper3, lower3}
 *   s0       — today's spot price (used for the horizontal reference line)
 *   isDark   — boolean for theme
 */
export default function GBMChart({ bands, s0, isDark }) {
  if (!bands?.length) return null;

  const textColor = isDark ? '#c8d8e0' : '#5f5f5f';
  const gridColor = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
  const indigo = '#6366f1';

  // Theme-aware band fills — darker in light mode (more visible on white), lighter in dark
  const bandFills = isDark
    ? { b1: 'rgba(99,102,241,0.15)', b2: 'rgba(99,102,241,0.09)', b3: 'rgba(99,102,241,0.05)' }
    : { b1: 'rgba(99,102,241,0.35)', b2: 'rgba(99,102,241,0.20)', b3: 'rgba(99,102,241,0.12)' };

  const last = bands[bands.length - 1];

  // Y-axis ticks: S0, expected at expiry, ±1σ and ±3σ outer bounds — deduplicated + sorted
  const rawTicks = [s0, last.expected, last.upper1, last.lower1, last.upper3, last.lower3];
  const yTicks = [...new Set(rawTicks.map((v) => Math.round(v)))].sort((a, b) => a - b);

  // Transform data so each row has range keys [lower, upper] for range areas
  const chartData = bands.map((d) => ({
    day: d.day,
    expected: d.expected,
    band3: [d.lower3, d.upper3],
    band2: [d.lower2, d.upper2],
    band1: [d.lower1, d.upper1],
  }));

  const formatPrice = (v) =>
    typeof v === 'number'
      ? v.toLocaleString('en-IN', { maximumFractionDigits: 0 })
      : v;

  // ── Sigma % labels drawn inside the band areas via Customized ────────────
  // Placed at ~65% along the x-axis, at the midpoint between adjacent band edges.
  const BandLabels = ({ yAxisMap, xAxisMap }) => {
    const yAxis = yAxisMap ? Object.values(yAxisMap)[0] : null;
    const xAxis = xAxisMap ? Object.values(xAxisMap)[0] : null;
    if (!yAxis?.scale || !xAxis?.scale) return null;

    const targetIdx = Math.min(Math.floor(bands.length * 0.65), bands.length - 1);
    const b = bands[targetIdx];
    const lx = xAxis.scale(b.day);

    const color = isDark ? 'rgba(148,163,216,0.55)' : 'rgba(79,70,229,0.60)';
    const entries = [
      { pct: '68%',   mid: (b.upper1 + b.lower1) / 2 },
      { pct: '95%',   mid: (b.upper2 + b.upper1) / 2 },
      { pct: '99.7%', mid: (b.upper3 + b.upper2) / 2 },
    ];

    return (
      <g>
        {entries.map(({ pct, mid }) => {
          const ly = yAxis.scale(mid);
          if (isNaN(lx) || isNaN(ly)) return null;
          return (
            <text
              key={pct}
              x={lx}
              y={ly}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={color}
              fontSize={8}
              fontWeight={700}
            >
              {pct}
            </text>
          );
        })}
      </g>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const exp = payload.find((p) => p.dataKey === 'expected')?.value;
    const b1  = payload.find((p) => p.dataKey === 'band1')?.value;
    const b2  = payload.find((p) => p.dataKey === 'band2')?.value;
    const b3  = payload.find((p) => p.dataKey === 'band3')?.value;
    return (
      <div
        style={{
          background: isDark ? '#122833' : '#fff',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
          borderRadius: 6,
          padding: '8px 10px',
          fontSize: 11,
          color: textColor,
          minWidth: 160,
        }}
      >
        <p style={{ fontWeight: 600, marginBottom: 4 }}>Day {label}</p>
        {exp != null && <p>Expected: <b>{formatPrice(exp)}</b></p>}
        {b1  && <p style={{ color: 'rgba(99,102,241,0.9)' }}>±1σ: {formatPrice(b1[0])} – {formatPrice(b1[1])}</p>}
        {b2  && <p style={{ color: 'rgba(99,102,241,0.7)' }}>±2σ: {formatPrice(b2[0])} – {formatPrice(b2[1])}</p>}
        {b3  && <p style={{ color: 'rgba(99,102,241,0.5)' }}>±3σ: {formatPrice(b3[0])} – {formatPrice(b3[1])}</p>}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={chartData} margin={{ top: 10, right: 72, bottom: 20, left: 6 }}>
        <CartesianGrid stroke={gridColor} />
        <XAxis
          dataKey="day"
          tick={{ fontSize: 10, fill: textColor }}
          label={{ value: 'Days to Expiry', position: 'insideBottom', offset: -10, fill: textColor, fontSize: 10 }}
        />
        <YAxis
          orientation="right"
          domain={['auto', 'auto']}
          ticks={yTicks}
          tick={{ fontSize: 8.5, fill: textColor }}
          tickFormatter={formatPrice}
          width={68}
        />
        <Tooltip content={<CustomTooltip />} />

        {/* 3σ band — outermost, most transparent */}
        <Area
          dataKey="band3"
          stroke="none"
          fill={bandFills.b3}
          isAnimationActive={false}
        />

        {/* 2σ band */}
        <Area
          dataKey="band2"
          stroke="none"
          fill={bandFills.b2}
          isAnimationActive={false}
        />

        {/* 1σ band */}
        <Area
          dataKey="band1"
          stroke="none"
          fill={bandFills.b1}
          isAnimationActive={false}
        />

        {/* Expected path */}
        <Line
          dataKey="expected"
          stroke={indigo}
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />

        {/* Today's spot — horizontal dashed reference */}
        <ReferenceLine
          y={s0}
          stroke={isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)'}
          strokeDasharray="4 3"
          label={{ value: 'S₀', position: 'insideTopLeft', fontSize: 10, fill: textColor }}
        />

        {/* Sigma % labels inside band areas */}
        <Customized component={BandLabels} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
